import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  Crown,
  Check,
  Sparkles,
  Zap,
  Gift,
  Loader2,
  AlertCircle,
  Star,
  Users,
  BookOpen
} from 'lucide-react'
import { commerceApi, formatPrice, getBillingPeriodLabel, type ProductResponse } from '@/api/commerce'

export function SubscriptionPlansPage() {
  const navigate = useNavigate()
  const [selectedProductId, setSelectedProductId] = useState<number | null>(null)
  const [selectedPricingId, setSelectedPricingId] = useState<number | null>(null)

  // 상품 목록 조회
  const { data: products = [], isLoading, error } = useQuery({
    queryKey: ['products'],
    queryFn: commerceApi.getProducts,
    staleTime: 5 * 60 * 1000,
  })

  const handleSelectPlan = (productId: number, pricingId: number) => {
    setSelectedProductId(productId)
    setSelectedPricingId(pricingId)
  }

  const handleProceedToCheckout = () => {
    if (selectedProductId && selectedPricingId) {
      navigate(`/checkout/${selectedProductId}?pricing=${selectedPricingId}`)
    }
  }

  if (isLoading) {
    return (
      <div className="flex flex-col justify-center items-center min-h-screen bg-linear-to-br via-purple-900 from-slate-900 to-slate-900">
        <Loader2 className="mb-4 w-12 h-12 text-purple-400 animate-spin" />
        <p className="text-lg text-purple-200">구독 플랜을 불러오는 중...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col justify-center items-center min-h-screen bg-linear-to-br via-purple-900 from-slate-900 to-slate-900">
        <AlertCircle className="mb-4 w-16 h-16 text-red-400" />
        <p className="mb-2 text-xl font-medium text-white">플랜을 불러올 수 없습니다</p>
        <p className="text-purple-200">잠시 후 다시 시도해주세요</p>
      </div>
    )
  }

  // API에서 실제 상품 사용 (Mock 상품은 사용하지 않음)
  const displayProducts: ProductResponse[] = products

  return (
    <div className="px-4 py-16 min-h-screen bg-linear-to-br via-purple-900 from-slate-900 to-slate-900">
      {/* Background decoration */}
      <div className="overflow-hidden absolute inset-0 pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 rounded-full blur-3xl bg-purple-500/20" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 rounded-full blur-3xl bg-blue-500/20" />
      </div>

      <div className="relative z-10 mx-auto max-w-6xl">
        {/* Header */}
        <div className="mb-16 text-center">
          <div className="inline-flex gap-2 items-center px-4 py-2 mb-6 text-sm font-medium text-purple-300 rounded-full bg-purple-500/20">
            <Sparkles className="w-4 h-4" />
            구독 시작하기
          </div>
          <h1 className="mb-4 text-4xl font-bold text-white md:text-5xl">
            당신에게 맞는 플랜을 선택하세요
          </h1>
          <p className="mx-auto max-w-2xl text-xl text-purple-200">
            지금 바로 깨봉의 모든 수학 강의를 무제한으로 학습해보세요
          </p>
        </div>

        {/* Plans Grid */}
        <div className="grid gap-8 mb-12 md:grid-cols-2 lg:grid-cols-3">
          {displayProducts.map((product) => (
            <ProductCard
              key={product.id}
              product={product}
              selectedPricingId={selectedProductId === product.id ? selectedPricingId : null}
              onSelect={handleSelectPlan}
            />
          ))}
        </div>

        {/* CTA Button */}
        {selectedProductId && selectedPricingId && (
          <div className="fixed right-0 bottom-0 left-0 p-4 bg-linear-to-t to-transparent from-slate-900 via-slate-900/95">
            <div className="mx-auto max-w-md">
              <button
                onClick={handleProceedToCheckout}
                className="flex gap-2 justify-center items-center px-8 py-4 w-full text-lg font-semibold text-white bg-linear-to-r from-purple-600 to-pink-600 rounded-xl shadow-lg transition-all hover:from-purple-500 hover:to-pink-500 shadow-purple-500/25"
              >
                <Zap className="w-5 h-5" />
                결제 진행하기
              </button>
            </div>
          </div>
        )}

        {/* Features Section */}
        <div className="p-8 mt-16 rounded-2xl border backdrop-blur-xs bg-white/5 border-white/10">
          <h2 className="mb-8 text-2xl font-bold text-center text-white">
            모든 구독에 포함된 혜택
          </h2>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {[
              { icon: '📚', title: '무제한 강의', desc: '모든 커리큘럼 접근' },
              { icon: '🎯', title: '맞춤 학습', desc: 'AI 기반 추천' },
              { icon: '📊', title: '진도 관리', desc: '학습 현황 분석' },
              { icon: '💬', title: '질문 지원', desc: '전문가 Q&A' }
            ].map((feature, idx) => (
              <div key={idx} className="text-center">
                <div className="mb-3 text-4xl">{feature.icon}</div>
                <h3 className="mb-1 font-semibold text-white">{feature.title}</h3>
                <p className="text-sm text-purple-300">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* FAQ Section */}
        <div className="mt-12 text-sm text-center text-purple-300">
          <p>* 테스트 모드: 실제 결제가 진행되지 않습니다</p>
          <p className="mt-2">언제든지 구독을 취소할 수 있습니다</p>
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// Sub Components
// ============================================================================

interface ProductCardProps {
  product: ProductResponse
  selectedPricingId: number | null
  onSelect: (productId: number, pricingId: number) => void
}

function ProductCard({ product, selectedPricingId, onSelect }: ProductCardProps) {
  const defaultPricing = product.pricingOptions.find(p => p.isDefault) || product.pricingOptions[0]
  const isPopular = product.pricingOptions.some(p => p.isDefault)
  
  // 내부에서 선택된 가격 옵션
  const [internalPricingId, setInternalPricingId] = useState<number | null>(
    selectedPricingId || defaultPricing?.id || null
  )
  
  const currentPricing = product.pricingOptions.find(p => p.id === internalPricingId) || defaultPricing

  const handlePricingChange = (pricingId: number) => {
    setInternalPricingId(pricingId)
    onSelect(product.id, pricingId)
  }

  const isSelected = selectedPricingId !== null

  return (
    <div
      className={`relative rounded-2xl p-6 transition-all ${
        isSelected
          ? 'bg-linear-to-br border-2 border-purple-400 shadow-xl from-purple-600/30 to-pink-600/30 shadow-purple-500/20'
          : 'border bg-white/10 border-white/20 hover:bg-white/15'
      }`}
    >
      {/* Popular Badge */}
      {isPopular && (
        <div className="flex absolute -top-3 left-1/2 gap-1 items-center px-4 py-1 text-xs font-bold bg-linear-to-r from-yellow-400 to-orange-400 rounded-full -translate-x-1/2 text-slate-900">
          <Crown className="w-3 h-3" />
          인기
        </div>
      )}

      {/* Product Header */}
      <div className="mb-6 text-center">
        <div className="flex justify-center items-center mx-auto mb-4 w-16 h-16 rounded-2xl bg-purple-500/20">
          <Gift className="w-8 h-8 text-purple-400" />
        </div>
        <h3 className="mb-2 text-xl font-bold text-white">{product.name}</h3>
        <p className="text-sm text-purple-300">{product.shortDescription || '수학의 모든 것'}</p>
        {product.curriculumTitle && (
          <div className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-500/20 rounded-lg">
            <BookOpen className="w-3.5 h-3.5 text-blue-400" />
            <span className="text-xs font-medium text-blue-300">{product.curriculumTitle}</span>
          </div>
        )}
      </div>

      {/* Pricing Options */}
      {product.pricingOptions.length > 1 && (
        <div className="mb-6">
          <div className="flex gap-2 justify-center">
            {product.pricingOptions.map((pricing) => (
              <button
                key={pricing.id}
                onClick={() => handlePricingChange(pricing.id)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                  internalPricingId === pricing.id
                    ? 'bg-purple-500 text-white'
                    : 'bg-white/10 text-purple-300 hover:bg-white/20'
                }`}
              >
                {getBillingPeriodLabel(pricing.billingPeriod)}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Price Display */}
      {currentPricing && (
        <div className="mb-6 text-center">
          <div className="flex gap-2 justify-center items-baseline">
            <span className="text-4xl font-bold text-white">
              {formatPrice(currentPricing.price, currentPricing.currency)}
            </span>
            <span className="text-purple-300">
              /{getBillingPeriodLabel(currentPricing.billingPeriod)}
            </span>
          </div>
          {currentPricing.originalPrice && (
            <div className="flex gap-2 justify-center items-center mt-1">
              <span className="text-sm text-purple-400 line-through">
                {formatPrice(currentPricing.originalPrice, currentPricing.currency)}
              </span>
              {currentPricing.discountPercentage && (
                <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs font-medium rounded-sm">
                  {currentPricing.discountPercentage}% 할인
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {/* Features */}
      <ul className="mb-6 space-y-3">
        {getFeatures(product).map((feature, idx) => (
          <li key={idx} className="flex gap-3 items-center text-purple-200">
            <Check className="shrink-0 w-4 h-4 text-green-400" />
            <span className="text-sm">{feature}</span>
          </li>
        ))}
      </ul>

      {/* Select Button */}
      <button
        onClick={() => currentPricing && handlePricingChange(currentPricing.id)}
        className={`w-full py-3 rounded-xl font-semibold transition-all ${
          isSelected
            ? 'text-white bg-purple-500'
            : 'text-white bg-white/10 hover:bg-white/20'
        }`}
      >
        {isSelected ? (
          <span className="flex gap-2 justify-center items-center">
            <Check className="w-4 h-4" />
            선택됨
          </span>
        ) : (
          '선택하기'
        )}
      </button>

      {/* Info */}
      {currentPricing && (
        <div className="flex gap-4 justify-center items-center mt-4 text-xs text-purple-400">
          <span className="flex gap-1 items-center">
            <Users className="w-3 h-3" />
            {currentPricing.maxSeats}인
          </span>
          {currentPricing.hasTrial && (
            <span className="flex gap-1 items-center">
              <Star className="w-3 h-3" />
              {currentPricing.trialDays}일 체험
            </span>
          )}
        </div>
      )}
    </div>
  )
}

// ============================================================================
// Helper Functions
// ============================================================================

function getFeatures(_product: ProductResponse): string[] {
  // 실제로는 product의 features 필드에서 가져올 수 있음
  return [
    '모든 커리큘럼 무제한 접근',
    '개인 맞춤 학습 추천',
    '진도 관리 및 분석',
    '오프라인 다운로드',
    '우선 고객 지원'
  ]
}


